import {Component, model, OnDestroy, OnInit} from '@angular/core';
import {BaseComponent} from '../../core/component/base.component';
import {PurchaseProcess, PurchaseProcessLine, PurchaseProcessStatus} from '../../model/purchase-process';
import {PurchaseProcessFilter} from '../../filter/purchase-process.filter';
import {PurchaseProcessService} from '../../service/purchase-process.service';
import {AlertService} from '../../core/service/alert.service';
import {MessageService} from '../../core/service/message.service';
import {Router} from '@angular/router';
import {SecurityService} from '../../core/service/security.service';
import {LoggerService} from '../../service/logger.service';
import {PurchaseRequestService} from '../../service/purchase-request.service';
import {PurchaseRequest} from '../../model/purchase-request';

@Component({
    selector: 'app-purchase-process',
    templateUrl: './purchase-process.component.html',
    standalone: false
})
export class PurchaseProcessComponent extends BaseComponent<PurchaseProcess> implements OnInit, OnDestroy {

  purchaseRequests: PurchaseRequest[] = [];
  selectedPurchaseRequest: PurchaseRequest | null = null;
  selectedRequestAmount?: number;
  savedStatus?: PurchaseProcessStatus;
  searchText = '';
  selectedStatus = '';

  statusLabels: Record<string, string> = {
    [PurchaseProcessStatus.PUBLISHED]: 'Publicado',
    [PurchaseProcessStatus.OFFER_RECEPTION]: 'Recepción de Ofertas',
    [PurchaseProcessStatus.EVALUATION]: 'Evaluación',
    [PurchaseProcessStatus.AWARDED]: 'Adjudicado',
    [PurchaseProcessStatus.FIRMLY_AWARDED]: 'Firme Adjudicado',
    [PurchaseProcessStatus.CONTRACT]: 'Contrato',
    [PurchaseProcessStatus.UNSUCCESSFUL]: 'Infructuoso',
    [PurchaseProcessStatus.DESERTED]: 'Desierto',
    [PurchaseProcessStatus.CLEARED]: 'Liberado'
  };

  constructor(private purchaseProcessService: PurchaseProcessService,
              private alertService: AlertService,
              protected messageService: MessageService,
              protected loggerService: LoggerService,
              protected router: Router,
              protected securityService: SecurityService,
              protected purchaseRequestService: PurchaseRequestService) {
    super(purchaseProcessService, 'purchaseProcess', alertService, router, securityService, messageService, loggerService);
  }

  getAvailableStatuses(): PurchaseProcessStatus[] {
    if (!this.model.id) {
      return [PurchaseProcessStatus.PUBLISHED];
    }

    const currentStatus = this.savedStatus;
    const available: PurchaseProcessStatus[] = [];

    if (currentStatus) {
      available.push(currentStatus);
    }

    switch (currentStatus) {
      case PurchaseProcessStatus.PUBLISHED:
        available.push(PurchaseProcessStatus.OFFER_RECEPTION);
        break;
      case PurchaseProcessStatus.OFFER_RECEPTION:
        available.push(PurchaseProcessStatus.EVALUATION);
        break;
      case PurchaseProcessStatus.EVALUATION:
        available.push(PurchaseProcessStatus.AWARDED);
        break;
      case PurchaseProcessStatus.AWARDED:
        available.push(PurchaseProcessStatus.FIRMLY_AWARDED);
        break;
      case PurchaseProcessStatus.FIRMLY_AWARDED:
        available.push(PurchaseProcessStatus.CONTRACT);
        break;
      case PurchaseProcessStatus.CONTRACT:
        available.push(PurchaseProcessStatus.CLEARED);
    }

    // From EVALUATION also move to UNSUCCESSFUL or DESERTED
    if (currentStatus === PurchaseProcessStatus.EVALUATION) {
      available.push(PurchaseProcessStatus.UNSUCCESSFUL);
      available.push(PurchaseProcessStatus.DESERTED);
    }

    // Remove duplicates just in case
    return [...new Set(available)];
  }

  editEvent(model: PurchaseProcess) {
    this.loadPurchaseRequests();
    super.editEvent(model);
  }

  createEvent() {
    this.loadPurchaseRequests();
    super.createEvent();
  }

  isAmountDisabled(): boolean {
    return this.model.status !== PurchaseProcessStatus.AWARDED;
  }

  isAwardDateDisabled(): boolean {
    return this.model.status !== PurchaseProcessStatus.AWARDED;
  }

  isUnsuccessfulDateDisabled(): boolean {
    return this.model.status !== PurchaseProcessStatus.UNSUCCESSFUL && this.model.status !== PurchaseProcessStatus.DESERTED;
  }

  getModelInstance(): PurchaseProcess {
    const process = new PurchaseProcess();
    process.status = PurchaseProcessStatus.PUBLISHED;
    return process;
  }

  protected cleanData() {

    this.selectedPurchaseRequest = null;
    this.selectedRequestAmount = undefined;
    this.loadPurchaseRequests();
  }

  clearFormErrors(): Map<string, string> {
    return new Map<string, string>();
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  getPageEvent(pageNumber: number, refresh: boolean = false): void {
    const filter = this.buildFilter();
    this.loading = true;
    this.purchaseProcessService.searchByFilter(filter, pageNumber, this.pageSize, this.sortBy, this.sortOrder)
      .subscribe({
        next: page => this.setPageEvent(page),
        error: err => this.processError(err)
      });
  }

  searchEvent(): void {
    this.getPageEvent(0, true);
  }

  private buildFilter(): PurchaseProcessFilter {
    const filter = new PurchaseProcessFilter();
    if (this.searchText && this.searchText.trim().length > 0) {
      filter.text = this.searchText.trim();
    }
    if (this.selectedStatus && this.selectedStatus.length > 0) {
      filter.status = this.selectedStatus;
    }
    return filter;
  }

  private loadPurchaseRequests() {
    this.purchaseRequestService.getAll(true).subscribe(
      {
        next: (v) => {
          this.purchaseRequests = v;
          this.onPurchaseRequestChange(false);
        },
        error: (e) => this.processError(e)
      }
    );
  }

  onPurchaseRequestChange(defaultAmount: boolean = true): void {
    if (this.model.purchaseRequest) {
      this.selectedPurchaseRequest = this.purchaseRequests.find(pr => pr.id === Number(this.model.purchaseRequest)) || null;
      if (this.selectedPurchaseRequest) {
        if (defaultAmount) {
          this.model.purchaseAmount = this.selectedPurchaseRequest.totalAmountToCommit;
          if (this.selectedPurchaseRequest.lines) {
            this.model.lines = this.selectedPurchaseRequest.lines.map(prl => {
              const line = new PurchaseProcessLine();
              line.purchaseRequestLine = prl.id;
              line.purchaseAmount = prl.amountToCommit;
              return line;
            });
          }
        } else {
          // If we are loading an existing model, we should still ensure selectedRequestAmount is set
          // and we might need to map accounts or other info from selectedPurchaseRequest lines if needed by UI
        }
        this.selectedRequestAmount = this.selectedPurchaseRequest.totalAmountToCommit;
      } else {
        this.selectedRequestAmount = undefined;
      }
    } else {
      this.selectedPurchaseRequest = null;
      this.selectedRequestAmount = undefined;
    }
  }

  calculateTotal(): void {
    this.model.purchaseAmount = this.model.lines.reduce((acc, line) => acc + (line.purchaseAmount || 0), 0);
  }

  getAmountToCommit(lineRequestLineId?: number): number | undefined {
    if (this.selectedPurchaseRequest && this.selectedPurchaseRequest.lines) {
      const line = this.selectedPurchaseRequest.lines.find(l => l.id === lineRequestLineId);
      return line ? line.amountToCommit : undefined;
    }
    return undefined;
  }

  loadEntity(id: number) {
    this.loading = true;
    this.purchaseProcessService.getWithDocuments(id).subscribe(
      {
        next: (res) => {
          this.model = res;
          this.onModelUpdate();
          this.loading = false;
        },
        error: (e) => this.processError(e)
      }
    );
  }

  onModelUpdate(): void {
    this.savedStatus = this.model.status;
    if (this.model.purchaseRequest) {
      this.purchaseRequestService.get(this.model.purchaseRequest).subscribe(
        {
          next: (v) => {
            this.selectedPurchaseRequest = v;
            this.purchaseRequests.push(v);
            this.onPurchaseRequestChange(false);
          },
          error: (e) => this.processError(e)
        }
      );
    } else {
      this.onPurchaseRequestChange();
    }
  }
}
